import { put, takeEvery, call } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import { Endpoints } from 'src/constants/endpoints'
import { ApiService, callSecureApi } from './api/callApi'
import {
  CreateContractorFile,
  DeleteContractorFile,
  FetchContractorFiles,
  UpdateContractorFile,
  FetchContractorFile,
  DownloadContractorFile,
} from 'src/constants/actionTypes'
import { ContractorFile, ContractorFileDocument } from 'src/interfaces/file'

interface FetchContractorFilesPayload {
  contractorId: string
}

function* onFetchContractorFiles(
  action: AsyncAction<FetchContractorFilesPayload, Array<ContractorFile>>,
) {
  try {
    const { contractorId } = action.payload
    const {
      payload,
    }: {
      payload: Array<ContractorFile>
    } = yield call(callSecureApi, Endpoints.FetchContractorFiles, {
      apiService: ApiService.Paraworks,
      method: 'GET',
      params: [
        {
          field: ':contractorId',
          value: contractorId,
        },
      ],
    })

    yield put(FetchContractorFiles.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch contractor files'
    yield put(FetchContractorFiles.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface FetchContractorFilePayload {
  fileId: string
  contractorId: string
}

function* onFetchContractorFile(
  action: AsyncAction<FetchContractorFilePayload, ContractorFileDocument>,
) {
  try {
    const { fileId, contractorId } = action.payload

    const {
      payload,
    }: {
      payload: ContractorFileDocument
    } = yield call(callSecureApi, Endpoints.FetchContractorFile, {
      apiService: ApiService.Paraworks,
      method: 'GET',
      params: [
        {
          field: ':fileId',
          value: fileId,
        },
        {
          field: ':contractorId',
          value: contractorId,
        },
      ],
    })

    yield put(FetchContractorFile.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch contractor file'
    yield put(FetchContractorFile.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface DownloadContractorFileRequest {
  fileId: string
  contractorId: string
}

function* onDownloadContractorFile(action: AsyncAction<DownloadContractorFileRequest, Buffer>) {
  const { fileId, contractorId } = action.payload

  try {
    const { payload }: { payload: Buffer } = yield call(
      callSecureApi,
      Endpoints.DownloadContractorFile,
      {
        apiService: ApiService.Paraworks,
        params: [
          {
            value: fileId,
            field: ':fileId',
          },
          {
            field: ':contractorId',
            value: contractorId,
          },
        ],
        returnBuffer: true,
      },
    )

    yield put(DownloadContractorFile.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to download file'

    yield put(DownloadContractorFile.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface CreateContractorFilePayload {
  contractorId: string
  file: File
  name: string
  expiresAt?: string
}

function* onCreateContractorFile(
  action: AsyncAction<CreateContractorFilePayload, ContractorFileDocument>,
) {
  const { contractorId, file, name, expiresAt } = action.payload

  const formData = new FormData()

  formData.append('file', file)
  formData.append('name', name)

  if (expiresAt) {
    formData.append('expiresAt', expiresAt)
  }

  try {
    const { payload }: { payload: ContractorFileDocument } = yield call(
      callSecureApi,
      Endpoints.CreateContractorFile,
      {
        apiService: ApiService.Paraworks,
        method: 'POST',
        body: formData,
        params: [
          {
            field: ':contractorId',
            value: contractorId,
          },
        ],
        resetContentType: true,
        returnResponse: true,
      },
    )

    yield put(CreateContractorFile.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to create file'

    yield put(CreateContractorFile.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UpdateContractorFilePayload {
  contractorId: string
  fileId: string
  name: string
  expiresAt?: string
}

function* onUpdateContractorFile(action: AsyncAction<UpdateContractorFilePayload, ContractorFile>) {
  const { contractorId, fileId, name, expiresAt } = action.payload

  try {
    const { payload }: { payload: ContractorFile } = yield call(
      callSecureApi,
      Endpoints.UpdateContractorFile,
      {
        apiService: ApiService.Paraworks,
        method: 'PUT',
        body: {
          name,
          expiresAt,
        },
        params: [
          {
            field: ':contractorId',
            value: contractorId,
          },
          {
            field: ':fileId',
            value: fileId,
          },
        ],
      },
    )

    yield put(UpdateContractorFile.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to update file'

    yield put(UpdateContractorFile.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface DeleteContractorFilePayload {
  contractorId: string
  fileId: string
}

function* onDeleteContractorFile(action: AsyncAction<DeleteContractorFilePayload, void>) {
  const { contractorId, fileId } = action.payload

  try {
    yield call(callSecureApi, Endpoints.DeleteContractorFile, {
      apiService: ApiService.Paraworks,
      returnResponse: true,
      method: 'DELETE',
      params: [
        {
          field: ':contractorId',
          value: contractorId,
        },
        {
          field: ':fileId',
          value: fileId,
        },
      ],
    })

    yield put(DeleteContractorFile.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to delete file'

    yield put(DeleteContractorFile.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* fileSaga() {
  yield takeEvery(FetchContractorFiles.type.REQUEST, onFetchContractorFiles)
  yield takeEvery(FetchContractorFile.type.REQUEST, onFetchContractorFile)
  yield takeEvery(DownloadContractorFile.type.REQUEST, onDownloadContractorFile)
  yield takeEvery(CreateContractorFile.type.REQUEST, onCreateContractorFile)
  yield takeEvery(UpdateContractorFile.type.REQUEST, onUpdateContractorFile)
  yield takeEvery(DeleteContractorFile.type.REQUEST, onDeleteContractorFile)
}
